import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/lib/auth-context';
import * as db from '@/lib/data-service';
import { MessageCircle } from 'lucide-react';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

export default function Messages() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [conversations, setConversations] = useState([]);
  const [longPressMatch, setLongPressMatch] = useState(null);
  const [exitTarget, setExitTarget] = useState(null);
  const [showExitDialog, setShowExitDialog] = useState(false);
  const [exitLoading, setExitLoading] = useState(false);
  const pressTimer = useRef(null);

  useEffect(() => {
    if (!user) return;

    let pollId = null;
    let cancelled = false;

    const refresh = async () => {
      await db.syncFromSupabase(user.id);
      await db.syncMessagesForUser(user.id);
      if (!cancelled) setConversations(db.getMatches(user.id));
    };

    refresh();
    pollId = setInterval(refresh, 3000);

    return () => {
      cancelled = true;
      if (pollId) clearInterval(pollId);
    };
  }, [user]);

  const startPress = (match) => {
    pressTimer.current = setTimeout(() => {
      setLongPressMatch(match);
    }, 600);
  };

  const cancelPress = () => {
    clearTimeout(pressTimer.current);
  };

  const openExitDialog = () => {
    setExitTarget(longPressMatch);
    setLongPressMatch(null);
    setShowExitDialog(true);
  };

  const handleExitChat = async () => {
    const target = exitTarget;
    if (!target) return;
    setExitLoading(true);
    const otherUserId = target.user_1 === user.id ? target.user_2 : target.user_1;
    const result = await db.exitChat(target.id, user.id, otherUserId);
    setExitLoading(false);
    setShowExitDialog(false);
    setExitTarget(null);
    if (result.success) {
      setConversations(prev => prev.filter(m => m.id !== target.id));
      toast('Chat ended.');
    } else {
      toast('Something went wrong. Try again.');
    }
  };

  if (conversations.length === 0) {
    return (
      <div className="h-full flex flex-col items-center justify-center px-6 text-center" data-testid="messages-empty">
        <MessageCircle className="h-12 w-12 text-muted-foreground/20 mb-4" />
        <p className="text-xl font-semibold text-foreground">No conversations yet</p>
        <p className="text-muted-foreground text-sm mt-1">Match with someone to start chatting</p>
      </div>
    );
  }

  return (
    <div className="h-full overflow-auto bg-background" data-testid="messages-screen">
      <div className="p-4">
        <h1 className="text-xl font-bold text-foreground mb-4">Messages</h1>
      </div>
      <div>
        {conversations.map(m => {
          const p = m.otherProfile;
          const photo = m.otherPhotos?.[0];
          const last = m.lastMessage;
          const isUnread = last && last.sender_id !== user.id;
          return (
            <button
              key={m.id}
              onClick={() => navigate(`/messages/${m.id}`)}
              onMouseDown={() => startPress(m)}
              onMouseUp={cancelPress}
              onMouseLeave={cancelPress}
              onTouchStart={() => startPress(m)}
              onTouchEnd={cancelPress}
              onTouchMove={cancelPress}
              className="w-full flex items-center gap-3 px-4 py-3 border-b border-border hover:bg-muted/30 transition-colors text-left"
              data-testid={`conversation-${m.id}`}
            >
              <div className="w-12 h-12 rounded-full overflow-hidden bg-muted shrink-0">
                {photo ? (
                  <img src={photo.url} className="w-full h-full object-cover" alt={p?.display_name} />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-muted-foreground font-bold">{p?.display_name?.[0]}</div>
                )}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between">
                  <span className={`text-sm ${isUnread ? 'font-bold text-foreground' : 'font-medium text-foreground'}`}>{p?.display_name}</span>
                  {last && <span className="text-[10px] text-muted-foreground shrink-0">{formatTime(last.created_at)}</span>}
                </div>
                <p className={`text-xs truncate mt-0.5 ${isUnread ? 'text-foreground font-medium' : 'text-muted-foreground'}`}>
                  {last ? (last.sender_id === user.id ? `You: ${last.content}` : last.content) : 'Say hello!'}
                </p>
              </div>
              {isUnread && <div className="w-2.5 h-2.5 bg-primary rounded-full shrink-0" />}
            </button>
          );
        })}
      </div>

      {/* Long-press action sheet */}
      <Sheet open={!!longPressMatch} onOpenChange={(open) => { if (!open) setLongPressMatch(null); }}>
        <SheetContent side="bottom" className="rounded-t-[20px]">
          <SheetHeader>
            <SheetTitle>{longPressMatch?.otherProfile?.display_name}</SheetTitle>
          </SheetHeader>
          <div className="py-4">
            <button
              onClick={openExitDialog}
              className="w-full text-left px-4 py-3 rounded-xl text-destructive hover:bg-destructive/10 transition-colors text-sm font-medium"
              data-testid="long-press-end-chat"
            >
              End chat
            </button>
          </div>
        </SheetContent>
      </Sheet>

      {/* Exit confirmation dialog */}
      <Dialog open={showExitDialog} onOpenChange={setShowExitDialog}>
        <DialogContent className="max-w-sm rounded-xl">
          <DialogHeader>
            <DialogTitle>End this conversation?</DialogTitle>
            <DialogDescription>This will permanently delete this chat and you won't see each other again. This cannot be undone.</DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex gap-2">
            <Button variant="outline" onClick={() => setShowExitDialog(false)} className="rounded-xl" disabled={exitLoading}>Cancel</Button>
            <Button variant="destructive" onClick={handleExitChat} className="rounded-xl" disabled={exitLoading} data-testid="confirm-exit-btn">
              {exitLoading ? 'Ending...' : 'End Chat'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function formatTime(dateStr) {
  const d = new Date(dateStr);
  const now = new Date();
  const diff = now - d;
  if (diff < 86400000) return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  if (diff < 7 * 86400000) return d.toLocaleDateString([], { weekday: 'short' });
  return d.toLocaleDateString([], { month: 'short', day: 'numeric' });
}
